<template>
	<figure class="cms-image-block" :style="figureStyle">
		<img
			v-if="src"
			v-bind="imgAttrs"
			:alt="alt"
			class="cms-image-block__img"
			:style="imgStyle"
		/>
		<div v-else class="cms-image-block__placeholder">
			<i class="fa fa-picture-o"></i>
			<span>No image set</span>
		</div>
		<figcaption v-if="caption || creditText" class="cms-image-block__caption">
			<span v-if="caption">{{ caption }}</span>
			<small v-if="creditText" class="cms-image-block__credit">{{ creditText }}</small>
		</figcaption>
	</figure>
</template>

<script>
/**
 * ImageBlock.vue
 * ──────────────
 * Displays an image with optional caption and border-radius.
 * Supports responsive sizing: separate width/height for desktop and mobile.
 *
 * Alignment, max-width, spacing and outer chrome are wrapper style traits.
 * The <figure> renders inline-block when the image has a fixed width so the
 * wrapper's text-align positions it; otherwise it fills the wrapper width.
 */

import { imgAttrs } from '../shared/imageSrc.js';
import { creditLine } from '../shared/imageCredit.js';

export default {
	name: 'ImageBlock',

	inject: {
		siteSettings: { default: function () { return {}; } }
	},

	props: {
		src: { type: String, default: '' },
		alt: { type: String, default: '' },
		/** Above the fold: load eagerly with high priority. */
		priority: { type: [Boolean, String], default: false },
		caption: { type: String, default: '' },
		/** Media-library file this image came from; the server fills `credit` from it. */
		fileID: { type: [Number, String], default: null },
		credit: { type: Object, default: null },
		showCredit: { type: String, default: 'site' },
		borderRadius: { type: String, default: '0' },
		// Responsive sizing
		imgWidth: { type: String, default: '' },
		imgHeight: { type: String, default: '' },
		imgMobileWidth: { type: String, default: '' },
		imgMobileHeight: { type: String, default: '' }
	},

	computed: {
		creditText: function () { return creditLine(this.credit, this.siteSettings, this.showCredit); },

		/** Responsive src/srcset; images inside a page column rarely exceed the page width. */
		imgAttrs: function () {
			return imgAttrs(this.src, { sizes: '(max-width: 768px) 100vw, min(100vw, 1280px)', priority: this.priority === true || this.priority === 'true' });
		},

		isMobile: function () {
			// In editor context, check Vuex; on frontend, check window width
			if (this.$store && this.$store.state.editorStore) {
				return this.$store.state.editorStore.viewportMode === 'mobile';
			}
			return (typeof window !== 'undefined') && window.innerWidth < 768;
		},

		/**
		 * Effective width/height after mobile overrides.
		 */
		resolvedSize: function () {
			var width = this.imgWidth || '';
			var height = this.imgHeight || '';
			if (this.isMobile) {
				if (this.imgMobileWidth) width = this.imgMobileWidth;
				if (this.imgMobileHeight) height = this.imgMobileHeight;
			}
			return { width: width, height: height };
		},

		figureStyle: function () {
			var s = {
				maxWidth: '100%'
			};
			if (this.resolvedSize.width) {
				s.display = 'inline-block';
				s.verticalAlign = 'top';
			} else {
				s.display = 'block';
				s.width = '100%';
			}
			return s;
		},

		imgStyle: function () {
			// Legacy content may still carry a block-level borderRadius; new
			// content gets the corner radius from the Border trait, which the
			// wrapper exposes as --block-radius.
			var s = { borderRadius: (this.borderRadius && this.borderRadius !== '0') ? this.borderRadius : 'var(--block-radius, var(--site-image-radius, 0))' };
			var width = this.resolvedSize.width;
			var height = this.resolvedSize.height;

			s.width = width || '100%';
			s.maxWidth = '100%';

			if (height) {
				s.height = height;
				s.objectFit = 'cover';
			} else {
				s.height = 'auto';
			}

			return s;
		}
	}
};
</script>

<style scoped>
.cms-image-block { margin: 0; padding: 0; text-align: inherit; }
.cms-image-block__img { display: block; width: 100%; height: auto; }
.cms-image-block__placeholder {
	display: flex; flex-direction: column; align-items: center; justify-content: center;
	width: 100%; min-height: 120px; background: #F4F4F5; border: 2px dashed #DDDEE0;
	border-radius: 4px; color: #999; gap: 0.5rem;
}
.cms-image-block__placeholder .fa { font-size: 2em; }
.cms-image-block__caption { margin-top: 0.5rem; font-size: 1em; color: #7f8c99; text-align: inherit; display: flex; flex-direction: column; gap: .15rem; }
.cms-image-block__credit { font-size: .78em; letter-spacing: .02em; opacity: .85; }
</style>
